import { loadStyle } from "./../utils/loadStyle";
import { createApp } from "vue";
import { createPinia } from "pinia";

import { i18n } from "./i18n-setup";

import FloatingVue from "floating-vue";
import "floating-vue/dist/style.css";
import { PiniaDebounce } from "@pinia/plugin-debounce";
import { useDebounceFn } from "@vueuse/core";
import { setupCalendar } from "v-calendar";
import { FontAwesomeIcon } from "./fontAwesome";

import App from "@/App.vue";

import { loadScript } from "../utils/loadScript";

import mitt from "mitt";

import Axios from "axios";
import { PerfectScrollbarPlugin } from "vue3-perfect-scrollbar";
import "vue3-perfect-scrollbar/style.css";

import TopolPlugin from "../../types/TopolPlugin";

import { useActiveMembersStore } from "@/stores/activeMembers";
import { getEmbedderHostname, setEmbedderOrigin } from "@topol/utils";
import { initBugsnag } from "./bugsnag";
//@ts-expect-error
import { worker } from "../mocks/browser";
declare global {
  interface Window {
    TopolPlugin: TopolPlugin;
  }
  interface Window {
    $event: {
      emit(key: string, payload?: unknown): void;
      on(key: string, handler: unknown): void;
      off(key: string, handler: unknown): void;
    };
  }
}

const mode = import.meta.env.MODE;

function installApp({ mode }: { mode: string }) {
  const pinia = createPinia();
  const app = createApp(App);

  pinia.use(PiniaDebounce(useDebounceFn));

  app.use(pinia);
  app.use(i18n);
  app.use(FloatingVue);
  app.use(PerfectScrollbarPlugin);
  app.use(setupCalendar, {});

  if (mode === "production" || mode === "staging") {
    const bugsnagVue = initBugsnag();

    if (bugsnagVue !== undefined) {
      app.use(bugsnagVue);
    }
  }

  // eslint-disable-next-line vue/component-definition-name-casing
  app.component("menu-content-container", MenuContentContainer);
  // eslint-disable-next-line vue/component-definition-name-casing
  app.component("content-row", ContentRow);
  // eslint-disable-next-line vue/component-definition-name-casing
  app.component("font-awesome-icon", FontAwesomeIcon);

  app.config.globalProperties["$http"] = Axios;

  // When ts ignore is not added it throws error
  // Type 'Emitter<Record<EventType, unknown>>' is not assignable to type 'MyEmitter'.

  // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error
  // @ts-ignore
  app.config.globalProperties["$event"] = emitter;

  app.mount("#app");
}

export default {
  init(type: string) {
    useGeneralStore().type = type;
  },
};

//Registering global components
import MenuContentContainer from "@/forms/components/MenuContentContainer/MenuContentContainer.vue";
import ContentRow from "@/forms/components/ContentRow/ContentRow.vue";
import { useGeneralStore } from "@/stores/generalStore";
import { useMjmlStore } from "@/stores/mjmlStore";

const emitter = mitt();
window.$event = emitter;

emitter.on("onOpenCustomBlockDialog", (payload) => {
  emitter.emit("updateCustomBlockContent", {
    content: "works?",
    //@ts-expect-error
    config: payload.config,
  });
});

loadScript(
  "https://cdn.jsdelivr.net/gh/ajaxorg/ace-builds@master/src-min-noconflict/ace.js"
);
loadStyle(
  "https://fonts.googleapis.com/css2?family=Ubuntu:wght@400;700&display=swap"
);
loadScript("https://d3ayqk5orocicw.cloudfront.net/dist/tim.js", false);
loadScript(
  `${import.meta.env.VITE_APP_AWS_FOLDER}/assets/tinymce/tinymce.min.js`
);

if (import.meta.env.MODE === "development") {
  worker.start({
    onUnhandledRequest: "bypass",
  });
  const optionsModule = await import("../../local-options/local-topol-options");

  const options = optionsModule.default();

  installApp({ mode: mode });

  (window as any).mjmlStore = useMjmlStore();

  useMjmlStore().setInitialLoader(options, "localhost:5173", "development");
} else if (import.meta.env.MODE === "testing") {
  worker.start({
    onUnhandledRequest: "bypass",
  });

  const optionsModule =
    await import("../../local-options/testing-topol-options");

  const options = optionsModule.default();

  installApp({ mode: mode });

  (window as any).mjmlStore = useMjmlStore();

  useMjmlStore().setInitialLoader(options, "localhost:5173", "testing");
} else {
  let lastLoadJson: unknown = null;

  window.addEventListener("message", function (event) {
    switch (event.data.action) {
      case "init": {
        setEmbedderOrigin(event.origin);

        installApp({ mode: mode });

        const embedderHostname = getEmbedderHostname();

        if (!embedderHostname) {
          console.error(
            "Cannot initialize the editor: unable to resolve the embedding page hostname (referrer is blocked and no parent origin is available)."
          );
          break;
        }

        useMjmlStore()
          .setInitialLoader(
            event.data.data.options,
            embedderHostname,
            "production"
          )
          .then(() => {
            if (lastLoadJson) {
              window.$event.emit("load", lastLoadJson);
            }
          })
          .catch((e) => {
            console.error(e);
            return false;
          });
        break;
      }
      case "load":
        lastLoadJson = event.data.data;

        window.$event.emit("load", event.data.data);
        break;
      case "save":
        window.$event.emit("save", event.data.data.lang);
        break;
      case "togglePreview":
        window.$event.emit("togglePreview");
        break;
      case "togglePreviewSize":
        window.$event.emit("togglePreviewSize");
        break;
      case "chooseFile":
        if (event.data.data.url) {
          window.$event.emit("chooseFile", event.data.data.url);
        }
        break;
      case "undo":
        window.$event.emit("undo");
        break;
      case "redo":
        window.$event.emit("redo");
        break;
      case "destroy":
        destroy();
        break;
      case "setSavedBlocks":
        if (event.data.data.savedBlocks) {
          window.$event.emit("setSavedBlocks", event.data.data.savedBlocks);
        }
        break;
      case "setPreviewHTML":
        window.$event.emit("setPreviewHTML", event.data.data.html);
        break;
      case "createNotification":
        window.$event.emit("createNotification", {
          ...event.data.data.notification,
          extarnalUse: true,
        });
        break;
      case "setActiveMembers":
        useActiveMembersStore().setActiveMembers(event.data.data.activeMembers);
        break;
      case "changeEmailToMobile":
        useMjmlStore().setIsMobileView(true);
        break;
      case "changeEmailToDesktop":
        useMjmlStore().setIsMobileView(false);
        break;
      case "toggleBlocksAndStructuresVisibility":
        useMjmlStore().blurHiddenElements = !useMjmlStore().blurHiddenElements;
        break;
      case "updateCustomBlockContent":
        useMjmlStore().updateCustomBlockContent({
          content: event.data.data.content,
        });
        break;
      case "refreshComments":
        window.$event.emit("refreshComments", event.data.data.key);
        break;
      case "refreshSyncedSections":
        window.$event.emit("refreshSyncedSections");
        break;
      case "openPremadeTemplatesSelection":
        window.$event.emit("openPremadeTemplatesSelection");
        break;
      case "updateApiAuthorizationHeader":
        window.$event.emit(
          "updateApiAuthorizationHeader",
          event.data.data.newHeader
        );
        break;
      case "setTemplateName":
        window.$event.emit("setTemplateName", event.data.data.name);
        break;
      case "updateOptions":
        window.$event.emit("updateOptions", event.data.data.options);
        break;
      case "setMergeTags":
        window.$event.emit("setMergeTags", event.data.data.mergeTags);
        break;
      case "toggleChatAI":
        window.$event.emit("toggleChatAI");
        break;
      case "toggleAutosaves":
        window.$event.emit("toggleAutosaves");
        break;
      case "toggleComments":
        window.$event.emit("toggleComments");
        break;
      case "createLanguage":
        window.$event.emit("createLanguage", event.data.data.lang);
        break;
      case "setPrimaryLanguage":
        window.$event.emit("setPrimaryLanguage", event.data.data.lang);
        break;
      case "deleteLanguage":
        window.$event.emit("deleteLanguage", event.data.data.lang);
        break;
      case "selectLanguage":
        window.$event.emit("selectLanguage", event.data.data.lang);
        break;
      case "translateLanguage":
        window.$event.emit("translateLanguage", {
          lang: event.data.data.lang,
          sourceLang: event.data.data.sourceLang,
        });
        break;
      case "translateAllLanguages":
        window.$event.emit("translateAllLanguages");
        break;
      case "getMutations":
        window.$event.emit("getMutations");
        break;
      case "toggleControlPanel":
        window.$event.emit("toggleControlPanel");
        break;
      case "updateTemplate":
        window.$event.emit("updateTemplate", {
          json: event.data.data.json,
          options: event.data.data.options,
        });
        break;
      case "toggleDarkMode":
        window.$event.emit("toggleDarkMode");
        break;
      default:
        break;
    }
  });
}

function destroy() {
  const el = document.getElementById("app");
  if (el) {
    while (el.firstChild) {
      el.removeChild(el.firstChild);
    }
  }
}
